'use client';

import Icon from '@ant-design/icons';
import classNames from 'classnames';

import StarSvg from '@/components/Icon/svgs/four_point_star.svg';
// import lockSvg from '@/components/Icon/svgs/lock.svg';
// import TipCard from '@/components/tip-card';
import type { SubscriptionStore } from '@/hooks/useSubscription';
import useSubscription from '@/hooks/useSubscription';
import { useTranslation } from '@/i18n/client';
import { EPlanAlias, EPlanPlatform } from '@/types/subscription';

import styles from './index.less';

export interface UsageCurrentKeyProps {
  className?: string;
}

export default function UsageCurrentKey(props: UsageCurrentKeyProps): JSX.Element | null {
  const { className } = props;
  const currentPlanDetail = useSubscription((state: SubscriptionStore) => state.currentPlanDetail);
  const { t } = useTranslation();

  if (!currentPlanDetail) {
    return null;
  }

  const { currentPlanAlias, nextPlanAlias, renewDate } = currentPlanDetail;

  const getBgText = (alias: EPlanAlias) => {
    if (alias === EPlanAlias.FREE) return t('menu.subscriptionModal.free');

    return t('menu.subscriptionModal.pro');
  };

  const isCurrentPlanSameAsNextPlan = currentPlanAlias === nextPlanAlias;
  const isFree = currentPlanAlias === EPlanAlias.FREE;

  return (
    <div className={classNames(styles.container, className)}>
      <div className="font-medium text-sm leading-6 text-primary-black">
        {t('menu.subscriptionModal.currentPlan')}
      </div>
      <div
        className={classNames(styles.cardContainer, {
          [styles.pro]: !isFree
        })}
      >
        <div className={classNames(styles.title, 'flex gap-3 items-center')}>
          {!isFree && <Icon className="text-[32px] w-8 h-8" component={StarSvg} />}
          {getBgText(currentPlanAlias)}
        </div>
        {/* <TipCard
          className={styles.tipCard}
          desc={
            <>
              <Icon className={styles.titleIcon} component={lockSvg} />
              <div>{t('menu.subscriptionModal.proDescription')}</div>
            </>
          }
        /> */}
        <div className={classNames(styles.nextPlan)}>
          <span>
            {isCurrentPlanSameAsNextPlan
              ? t('menu.subscriptionModal.currentPlanNoticeRenew', {
                  renewDate: renewDate
                })
              : t('menu.subscriptionModal.currentPlanNoticeEnds', {
                  renewDate: renewDate
                })}

            {/* {`Current plan ${isCurrentPlanSameAsNextPlan ? 'renews' : 'ends'} on ${renewDate}`} */}
          </span>
          {!isCurrentPlanSameAsNextPlan && (
            <span>
              {t('menu.subscriptionModal.nextCycle')}
              {getBgText(nextPlanAlias)}
            </span>
          )}
        </div>
      </div>

      {/* Apple Tips */}
      {currentPlanDetail.platform === EPlanPlatform.APPLE && (
        <div className="bg-[#EEE9E4] flex items-center px-4 py-3 gap-2 backdrop-blur-[40px] rounded-[8px] w-full box-border">
          <div className="text-xs leading-5 text-primary-black">
            {t('menu.subscriptionModal.appleTips')}
          </div>
          <div
            className="bg-primary-black shrink-0 transition-colors ml-auto text-primary-white rounded-[8px] h-7 box-border py-1 px-4 text-xs leading-5 cursor-pointer hover:bg-primary-sub-color"
            onClick={() => {
              window.open('https://apps.apple.com/account/subscriptions', '_blank');
            }}
          >
            {t('menu.subscriptionModal.manage')}
          </div>
        </div>
      )}

      {/* Google Tips */}
      {currentPlanDetail.platform === EPlanPlatform.GOOGLE && (
        <div className="bg-[#EEE9E4] flex items-center px-4 py-3 gap-2 backdrop-blur-[40px] rounded-[8px] w-full box-border">
          <div className="text-xs leading-5 text-primary-black">
            {t('menu.subscriptionModal.googleTips')}
          </div>
          <div
            className="bg-primary-black transition-colors shrink-0 ml-auto text-primary-white rounded-[8px] h-7 box-border py-1 px-4 text-xs leading-5 cursor-pointer hover:bg-primary-sub-color"
            onClick={() => {
              window.open('https://play.google.com/store/account/subscriptions', '_blank');
            }}
          >
            {t('menu.subscriptionModal.manage')}
          </div>
        </div>
      )}
    </div>
  );
}
